/**
 * The session's secondary facts (provider account, cost, headroom, why this agent) as one quiet
 * drillable row under the header chips, instead of a 150–270 px block above the title
 * (docs/ux/review/interaction-visual.md #3). Unknown values are left out of the summary line
 * rather than shown in warning colour; the expanded view keeps every figure.
 */
import { ChevronRight } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
  type AccountRef,
  chargeLabel,
  headroomLabel,
  type SessionUsage,
} from '../lib/usage-accounting.js'
import './run-details.css'

export function runDetailsSummary(
  usage: SessionUsage,
  account: AccountRef | undefined,
  now: number,
): string {
  const parts = [
    account ? account.label : undefined,
    usage.charge.kind === 'missing' ? undefined : chargeLabel(usage.charge),
    usage.headroom ? headroomLabel(usage.headroom, now) : undefined,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : 'Account, cost and routing'
}

export function RunDetails({
  usage,
  account,
  children,
}: {
  usage: SessionUsage
  account?: AccountRef
  /** The full details (usage strip, why this agent), rendered when expanded. */
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <details
      className="run-details vk-header__extra"
      data-testid="run-details"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="run-details__summary">
        <ChevronRight size={14} aria-hidden="true" className="run-details__chev" />
        <span className="sr-only">Run details: </span>
        <span>{runDetailsSummary(usage, account, Date.now())}</span>
      </summary>
      {open ? <div className="run-details__body">{children}</div> : null}
    </details>
  )
}
